import { useEffect } from 'react';

/**
 * Modal Behavior Hooks
 * Custom hooks for modal behaviors like escape key handling, body scroll prevention, etc.
 */

// =============================================================================
// Escape Key Hook
// =============================================================================

interface UseEscapeKeyProps {
  isOpen: boolean;
  onClose: () => void;
  enabled?: boolean;
}

/**
 * Hook to handle escape key press for closing modal
 * @param config - Escape key configuration
 */
export const useEscapeKey = ({ isOpen, onClose, enabled = true }: UseEscapeKeyProps) => {
  useEffect(() => {
    if (!isOpen || !enabled) {
      return;
    }

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, enabled, onClose]);
};

// =============================================================================
// Body Scroll Prevention Hook
// =============================================================================

interface UseBodyScrollLockProps {
  isOpen: boolean;
}

/**
 * Hook to prevent body scroll when modal is open
 * @param config - Body scroll lock configuration
 */
export const useBodyScrollLock = ({ isOpen }: UseBodyScrollLockProps) => {
  useEffect(() => {
    if (isOpen) {
      // Store original overflow value
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.overflow = originalOverflow || 'unset';
      };
    }
  }, [isOpen]);
};

// =============================================================================
// Focus Management Hook
// =============================================================================

interface UseFocusManagementProps {
  isOpen: boolean;
  modalRef?: React.RefObject<HTMLDivElement | null>;
  restoreFocus?: boolean;
}

/**
 * Hook to manage focus within modal (accessibility)
 * @param config - Focus management configuration
 */
export const useFocusManagement = ({
  isOpen,
  modalRef,
  restoreFocus = true,
}: UseFocusManagementProps) => {
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const previousActiveElement = document.activeElement as HTMLElement;

    // Focus first focusable element in modal
    const focusFirstElement = () => {
      if (modalRef?.current) {
        const focusableElements = modalRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const firstElement = focusableElements[0] as HTMLElement;
        if (firstElement) {
          firstElement.focus();
        }
      }
    };

    // Trap focus within modal
    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !modalRef?.current) {
        return;
      }

      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };

    // Set initial focus
    setTimeout(focusFirstElement, 100);

    // Add event listener for tab key
    document.addEventListener('keydown', handleTabKey);

    return () => {
      document.removeEventListener('keydown', handleTabKey);

      // Restore focus to previous element
      if (restoreFocus && previousActiveElement) {
        previousActiveElement.focus();
      }
    };
  }, [isOpen, modalRef, restoreFocus]);
};

// =============================================================================
// Backdrop Click Handler
// =============================================================================

interface UseBackdropClickProps {
  enabled: boolean;
  onClose: () => void;
}

/**
 * Creates a backdrop click handler
 * @param config - Backdrop click configuration
 * @returns Click handler function
 */
export const useBackdropClick = ({ enabled, onClose }: UseBackdropClickProps) => {
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (enabled && e.target === e.currentTarget) {
      onClose();
    }
  };

  return handleBackdropClick;
};

// =============================================================================
// Combined Modal Behavior Hook
// =============================================================================

interface UseModalBehaviorProps {
  isOpen: boolean;
  onClose: () => void;
  closeOnEscape?: boolean;
  closeOnBackdrop?: boolean;
  lockBodyScroll?: boolean;
  manageFocus?: boolean;
  modalRef?: React.RefObject<HTMLDivElement | null>;
}

/**
 * Combined hook for all modal behaviors
 * @param config - Modal behavior configuration
 * @returns Backdrop click handler
 */
export const useModalBehavior = ({
  isOpen,
  onClose,
  closeOnEscape = true,
  closeOnBackdrop = true,
  lockBodyScroll = true,
  manageFocus = true,
  modalRef,
}: UseModalBehaviorProps) => {
  // Use individual hooks
  useEscapeKey({ isOpen, onClose, enabled: closeOnEscape });
  useBodyScrollLock({ isOpen: isOpen && lockBodyScroll });
  useFocusManagement({ isOpen, modalRef, restoreFocus: manageFocus });

  // Return backdrop click handler
  const handleBackdropClick = useBackdropClick({
    enabled: closeOnBackdrop,
    onClose,
  });

  return { handleBackdropClick };
};

// =============================================================================
// Portal Hook for Modal Rendering
// =============================================================================

interface UseModalPortalProps {
  portalId?: string;
}

/**
 * Hook to create or get modal portal element
 * @param config - Portal configuration
 * @returns Portal element
 */
export const useModalPortal = ({ portalId = 'modal-root' }: UseModalPortalProps = {}) => {
  const getPortalElement = () => {
    let portalElement = document.getElementById(portalId);

    if (!portalElement) {
      portalElement = document.createElement('div');
      portalElement.id = portalId;
      portalElement.style.position = 'relative';
      portalElement.style.zIndex = '9999';
      document.body.appendChild(portalElement);
    }

    return portalElement;
  };

  return getPortalElement();
};
